import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { useLoadingState } from '@ipfabric/ui-components/hooks/useLoadingState';
import {
  type LoadingState,
  withLoadingIndicator,
} from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import {
  type BuiltInIntentVerificationRuleSummary,
  type DefinedIntentVerificationRuleSummary,
  type GraphRequestBody,
  type IntentSummaryResponseBody,
  safeAbsurd,
} from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { useGraphContextSelectors } from 'components/Graph/GraphContext';
import {
  isPathLookupGraphMetadata,
  isTopologyGraphMetadata,
} from 'components/Graph/GraphState';
import { extractPresetSettings } from 'components/Graph/helpers/extractPresetSettings';
import { selectGraphHasContent } from 'components/Graph/model/selectors/selectGraphHasContent';
import { useEffect, useState } from 'react';
import { nonEmptyObjectOrUndefined } from 'utils/object/nonEmptyObjectOrUndefined';
import { useApi } from 'utils/request/useApi';

export interface IntentVerificationHook {
  builtInRules: {
    loadingState: LoadingState;
    summary: BuiltInIntentVerificationRuleSummary | null;
  };
  definedRules: {
    loadingState: LoadingState;
    summary: DefinedIntentVerificationRuleSummary | null;
  };
  loadIntents: () => void;
}

export const useIntentVerification = (): IntentVerificationHook => {
  const ctxProps = useGraphContextSelectors({
    attributeFilters: prop('attributeFilters'),
    graphHasContent: selectGraphHasContent,
    graphMetadata: prop('graphMetadata'),
    pathlookupPreset: prop('pathlookup', 'preset'),
    snapshotId: prop('snapshotId'),
    topologyPreset: prop('layoutSettings', 'preset'),
  });
  const apiRequest = useApi().request;
  const [definedSummary, setDefinedSummary] =
    useState<DefinedIntentVerificationRuleSummary | null>(null);
  const [definedLoadingState, setDefinedLoadingState] = useLoadingState();
  const [builtInSummary, setBuiltInSummary] =
    useState<BuiltInIntentVerificationRuleSummary | null>(null);
  const [builtInLoadingState, setBuiltInLoadingState] = useLoadingState();

  const loadDefinedSummary = async (data: GraphRequestBody) => {
    const summary = await withLoadingIndicator(setDefinedLoadingState)(() =>
      apiRequest<IntentSummaryResponseBody, GraphRequestBody>({
        url: '/graphs/intents/summary/defined',
        method: 'POST',
        data,
      }),
    );

    if (summary.definedRules === undefined) {
      stumble('Intent summary for defined rules returned no data');
      setDefinedSummary(null);
      setDefinedLoadingState('ERROR');
      return;
    }

    setDefinedSummary(summary.definedRules);
  };

  const loadBuiltInSummary = async (data: GraphRequestBody) => {
    const summary = await withLoadingIndicator(setBuiltInLoadingState)(() =>
      apiRequest<IntentSummaryResponseBody, GraphRequestBody>({
        url: '/graphs/intents/summary/built-in',
        method: 'POST',
        data,
      }),
    );

    if (summary.builtInRules === undefined) {
      stumble('Intent summary for built in rules returned no data');
      setBuiltInSummary(null);
      setBuiltInLoadingState('ERROR');
      return;
    }

    setBuiltInSummary(summary.builtInRules);
  };

  const loadIntents = () => {
    if (!ctxProps.graphHasContent || ctxProps.graphMetadata === null) {
      setDefinedSummary(null);
      setDefinedLoadingState('INITIAL');
      setBuiltInSummary(null);
      setBuiltInLoadingState('INITIAL');
    } else if (isPathLookupGraphMetadata(ctxProps.graphMetadata)) {
      loadDefinedSummary({
        attributeFilters: nonEmptyObjectOrUndefined(ctxProps.attributeFilters),
        parameters: ctxProps.graphMetadata.parameters,
        settings: extractPresetSettings(ctxProps.pathlookupPreset),
        snapshot: ctxProps.snapshotId,
      });
      setBuiltInSummary(null);
      setBuiltInLoadingState('INITIAL');
    } else if (isTopologyGraphMetadata(ctxProps.graphMetadata)) {
      loadDefinedSummary({
        attributeFilters: nonEmptyObjectOrUndefined(ctxProps.attributeFilters),
        parameters: ctxProps.graphMetadata.parameters,
        settings: extractPresetSettings(ctxProps.topologyPreset),
        snapshot: ctxProps.snapshotId,
      });
      loadBuiltInSummary({
        attributeFilters: nonEmptyObjectOrUndefined(ctxProps.attributeFilters),
        parameters: ctxProps.graphMetadata.parameters,
        settings: extractPresetSettings(ctxProps.topologyPreset),
        snapshot: ctxProps.snapshotId,
      });
    } else {
      safeAbsurd(ctxProps.graphMetadata);
    }
  };

  const getApplicablePreset = () => {
    if (!ctxProps.graphHasContent || ctxProps.graphMetadata === null) {
      return null;
    }
    if (isPathLookupGraphMetadata(ctxProps.graphMetadata)) {
      return ctxProps.pathlookupPreset;
    }
    if (isTopologyGraphMetadata(ctxProps.graphMetadata)) {
      return ctxProps.topologyPreset;
    }
    safeAbsurd(ctxProps.graphMetadata);
    return null;
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: would need wider refactoring
  useEffect(() => {
    loadIntents();
  }, [
    ctxProps.graphHasContent,
    ctxProps.graphMetadata?.parameters,
    ctxProps.snapshotId,
    ctxProps.attributeFilters,
    getApplicablePreset(),
  ]);

  return {
    builtInRules: {
      loadingState: builtInLoadingState,
      summary: builtInSummary,
    },
    definedRules: {
      loadingState: definedLoadingState,
      summary: definedSummary,
    },
    loadIntents,
  };
};
